// src/features/datev-weiterberechnung/types.ts
//
// Feature: DATEV-Rechnung auslesen und bestimmte Posten an Kunden weiterberechnen
//
// Der User lädt die monatliche DATEV-Rechnung (PDF) hoch. Das System extrahiert
// pro Mandant die Posten, filtert auf die Whitelist und erzeugt/ergänzt die
// entsprechenden Rechnungsentwürfe im Invoice-System.

// ============================================
// WHITELIST
// ============================================

/**
 * Artikel, die an Kunden weiterberechnet werden.
 * Key = DATEV-Artikelnummer (String, da führende Nullen möglich wären)
 * Value = Bezeichnung für die Rechnungsposition
 *
 * Hinweis: DATEV führt für Porto-, Versand- und Druckleistungen getrennte
 * Artikelnummern je Abrechnungssystem (LuG und LODAS). Beide Varianten stehen
 * hier, damit Kunden unabhängig vom System korrekt weiterberechnet werden.
 */
export const WEITERBERECHNUNG_WHITELIST: Record<string, string> = {
  '91107': 'Arbeitnehmer online Meine Abrechnungen',
  '74044': 'Arbeitnehmer online Porto',
  '74031': 'ATVA Portopauschale Standardbrief (LuG)',
  '72053': 'ATVM Porto Sendung bis 500g (LuG)',
  '28871': 'Auftragsversand an Arbeitnehmer',
  '22850': 'Auftragsversand an Mandant',
  '22173': 'Druck-Einzelleistung',
  // LODAS-Gegenstücke
  '72049': 'ATVM Porto Sendung bis 500g (LODAS)',
  '72050': 'ATVM Porto Sendung 501-1.000g (LODAS)',
  '28873': 'Auftragsversand an Mandant',
  '22167': 'Druck-Einzelleistung',
};

/**
 * Prüft, ob eine Artikelnummer weiterberechnet werden soll
 */
export function isWhitelistArtikel(artikelnr: string): boolean {
  if (!artikelnr) return false;
  return Object.prototype.hasOwnProperty.call(WEITERBERECHNUNG_WHITELIST, artikelnr);
}

// ============================================
// BERATERBEZOGENER BLOCK
// ============================================

/**
 * Reservierte Kennung für den Block "Beraterbezogene Leistungen" der
 * DATEV-Rechnung. Dieser Block ist keinem Mandanten zugeordnet (Kosten der
 * Kanzlei selbst: Basis-Angebot, Schnittstellenberatung, Eilservice,
 * mIDentity-Sticks, Abrechnungsbonus). Er wird ausschliesslich für die
 * Auswertung erfasst und ist per Definition nie weiterberechenbar.
 */
export const BERATER_BLOCK_KEY = 'BERATERBEZOGEN';

/** Anzeigename des beraterbezogenen Blocks */
export const BERATER_BLOCK_LABEL = 'Beraterbezogene Leistungen (XTAX)';

/**
 * Eigener Bestand von XTAX in DATEV. Die Kosten dieses Mandanten sind keine
 * weiterberechenbaren Kundenkosten, sondern Kosten der Kanzlei selbst. Sie werden
 * deshalb dem beraterbezogenen Block zugeschlagen.
 *
 * Die Zuordnung greift nur, wenn BEIDE Angaben passen: die Beraternummer aus dem
 * Rechnungskopf und die Mandantennummer des Blocks. Bei einer Rechnung mit anderer
 * Beraternummer bleibt Mandant 10000 ein normaler Mandantenblock.
 */
export const EIGENE_FIRMA_BERATERNUMMER = '992453';
export const EIGENE_FIRMA_MANDANTENNUMMER = '10000';

/**
 * Bestandteil eines zusammengefuehrten Blocks. Dient der Nachvollziehbarkeit
 * gegenueber der DATEV-Rechnung, in der die Teilbetraege getrennt ausgewiesen sind.
 */
export interface BlockBestandteil {
  bezeichnung: string;
  summe: number;
}

// ============================================
// PARSER-OUTPUT
// ============================================

/**
 * Eine einzelne Position aus der DATEV-Rechnung (vor Aggregation)
 */
export interface ParsedInvoicePosition {
  artikelnr: string; // leer bei Pauschalzeilen ohne Artikelnummer (z.B. Abrechnungsbonus)
  bezeichnung: string;
  menge: number;
  leistungszeitpunkt: string; // z.B. "März 2026", "Aug. 2026" oder "23.03.2026"
  einzelpreisBerechnet: number; // gesamtpreis / menge
  gesamtpreis: number; // Netto
  ustSatz: number; // 19, 7, etc. (0 bei Pauschalzeilen ohne USt-Angabe)
}

/**
 * Eine aggregierte Position (mehrfach vorkommender Artikel zusammengefasst)
 */
export interface AggregatedPosition {
  artikelnr: string;
  bezeichnung: string; // Label aus der Whitelist
  menge: number; // Summe aller Mengen
  gesamtpreis: number; // Summe aller Gesamtpreise (netto)
  einzelpreis: number; // gesamtpreis / menge (neu berechnet)
  einheit: string; // "Stk"
}

/**
 * Eine aggregierte Position, die NICHT weiterberechnet wird.
 * Bezeichnung ist die DATEV-Originalbezeichnung, nicht das Whitelist-Label.
 * Aggregationsschlüssel ist Artikelnummer + USt-Satz, damit die Darstellung
 * der DATEV-Rechnung entspricht (ein Artikel kann in zwei USt-Sätzen erscheinen).
 */
export interface NonBillablePosition {
  artikelnr: string; // leer bei Pauschalzeilen ohne Artikelnummer
  bezeichnung: string;
  menge: number;
  ustSatz: number;
  gesamtpreis: number;
}

/**
 * Ein Mandantenblock aus der DATEV-Rechnung
 */
export interface ParsedMandant {
  datevMandantennummer: string; // z.B. "42588", bei Beraterblock BERATER_BLOCK_KEY
  datevName: string; // z.B. "PRO CONSULT"
  positionen: ParsedInvoicePosition[]; // alle Posten, ungefiltert
  gesamtbetragLautDatev: number; // "Summe: XXX" aus der DATEV-Rechnung (zur Kontrolle)
  istBeraterBlock: boolean; // true nur beim Block "Beraterbezogene Leistungen"
  /**
   * Teilbetraege, aus denen sich gesamtbetragLautDatev zusammensetzt.
   * Leer bei allen Bloecken, die eins zu eins aus der DATEV-Rechnung stammen.
   */
  zusammensetzung: BlockBestandteil[];
  /**
   * Zeilen des Blocks, die wie eine Postenzeile aussehen, vom Parser aber nicht
   * gelesen werden konnten. Jede Zeile hier bedeutet einen möglicherweise nicht
   * weiterberechneten Betrag und blockiert die Ausführung.
   */
  ungeparsteZeilen: string[];
}

/**
 * Komplettes Parse-Ergebnis der DATEV-Rechnung
 */
export interface ParsedDatevInvoice {
  month: number; // 1-12 (aus "März 2026")
  year: number; // 4-stellig
  rechnungsnummer: string | null; // aus PDF-Header, falls extrahiert
  rechnungsdatum: string | null; // aus PDF-Header (YYYY-MM-DD), falls extrahiert
  beraternummer: string | null; // aus PDF-Header, falls extrahiert
  mandanten: ParsedMandant[];
  /**
   * Summe aller "Nettobetrag"-Zeilen aus dem Rechnungsfuß. DATEV weist je
   * USt-Satz eine eigene Zeile aus, deshalb wird summiert. Dient als
   * Gegenprobe gegen die Summe aller Blockkopfsummen und deckt damit auch
   * einen komplett nicht erkannten Mandantenblock auf.
   * null, wenn der Rechnungsfuß nicht gelesen werden konnte.
   */
  nettoSummeLautPdf: number | null;
}

// ============================================
// MATCHING & ACTION PLANNING
// ============================================

/**
 * Kategorien, in die ein gemetchter DATEV-Mandant fallen kann
 */
export type MatchStatus =
  | 'ok' // Kunde gefunden, keine Sammelbeziehung, Weiterberechnungsposten vorhanden
  | 'no_whitelist_items' // Kunde gefunden, aber keine Whitelist-Posten in DATEV
  | 'sammelkunde_skipped' // Kunde hat auftraggeber_id → überspringen
  | 'customer_not_found' // DATEV-Mandantennr nicht in customers-Tabelle
  | 'customer_deleted' // Kunde ist soft-deleted
  | 'berater_block'; // Beraterbezogene Leistungen, keinem Mandanten zugeordnet

/**
 * Action, die beim Ausführen durchgeführt wird
 */
export type TargetAction =
  | 'append_draft' // Bestehenden Entwurf für year/month ergänzen
  | 'append_draft_next_month' // Bestehenden Entwurf für year/month+N (nächster freier Monat) ergänzen
  | 'create_draft' // Neuen Entwurf für year/month anlegen
  | 'create_draft_next_month' // Neuen Entwurf für year/month+N anlegen
  | 'skip'; // Kein Match oder Sammelkunde – nichts tun

/**
 * Zielrechnungs-Info für einen gematchten Kunden
 */
export interface TargetInvoiceInfo {
  action: TargetAction;
  targetYear: number;
  targetMonth: number;
  existingInvoiceId: string | null; // null wenn action = create_*
  existingInvoiceStatus: string | null; // Status der gefundenen Rechnung (für Info)
}

/**
 * Ein Match-Eintrag: DATEV-Mandant + gematchter XTAX-Kunde + geplante Aktion
 */
export interface MandantMatch {
  // DATEV-Rohdaten
  datevMandantennummer: string;
  datevName: string;

  // Matching-Ergebnis
  status: MatchStatus;
  customerId: string | null;
  customerCompany: string | null;

  // Weiterberechnungs-Daten (nur bei status='ok')
  aggregatedPositions: AggregatedPosition[];
  totalWeiterberechnung: number; // Summe der Netto-Beträge

  // Zielrechnung (nur bei status='ok')
  targetInvoice: TargetInvoiceInfo | null;

  // Hinweistext (z.B. bei Warnungen)
  warning: string | null;

  // ---- Analyse (nachgelagert, ohne Einfluss auf die Ausführung) ----

  /** true nur beim Block "Beraterbezogene Leistungen" */
  istBeraterBlock: boolean;

  /**
   * Whitelist-Posten, die NICHT weiterberechnet werden, weil der Kunde
   * übersprungen wird (Sammelkunde, nicht gefunden, gelöscht).
   * Bei status='ok' immer leer.
   */
  skippedBillablePositions: AggregatedPosition[];
  totalSkippedBillable: number;

  /** Posten, die nicht auf der Whitelist stehen (grundsätzlich nicht weiterberechenbar) */
  nonBillablePositions: NonBillablePosition[];
  totalNonBillable: number;

  /** "Summe:" aus dem DATEV-Blockkopf (maßgeblicher Anzeigewert) */
  datevSummeLautKopf: number;

  /** Summe aller geparsten Posten des Blocks */
  parsedSumme: number;

  /** Differenz parsedSumme - datevSummeLautKopf; null wenn innerhalb Toleranz */
  summenAbweichung: number | null;

  /**
   * Zeilen dieses Blocks, die wie eine Postenzeile aussehen, aber nicht gelesen
   * werden konnten. Nicht leer bedeutet: die Ausführung wird blockiert.
   */
  ungeparsteZeilen: string[];

  /** Teilbetraege eines zusammengefuehrten Blocks; leer bei allen anderen */
  zusammensetzung: BlockBestandteil[];
}

/**
 * Eine Zeile der Artikel-Rangliste (über die gesamte DATEV-Rechnung)
 */
export interface ArtikelRankingEntry {
  artikelnr: string; // leer bei Pauschalzeilen ohne Artikelnummer
  bezeichnung: string; // DATEV-Originalbezeichnung
  menge: number; // Summe aller Mengen über alle Blöcke
  gesamtpreis: number; // Summe aller Netto-Beträge
  vorkommen: number; // Anzahl der Blöcke, in denen der Artikel auftaucht
  aufWhitelist: boolean; // Artikel ist grundsätzlich weiterberechenbar
}

/**
 * Eine Zeile der Kunden-Rangliste (über die gesamte DATEV-Rechnung)
 */
export interface KundenRankingEntry {
  datevMandantennummer: string;
  datevName: string;
  customerCompany: string | null;
  status: MatchStatus;
  istBeraterBlock: boolean;
  datevSumme: number; // Kopfsumme laut DATEV (Sortier- und Anzeigewert)
  weiterberechnet: number; // wird tatsächlich weiterberechnet
  uebersprungenBerechenbar: number; // whitelistfähig, aber Kunde wird übersprungen
  nichtWeiterberechenbar: number; // nicht auf der Whitelist
  summenAbweichung: number | null;
}

/**
 * Gesamtergebnis der Analyse (für die Vorschau-Tabelle)
 */
export interface WeiterberechnungAnalysis {
  sourceMonth: number;
  sourceYear: number;
  matches: MandantMatch[];
  summary: {
    totalMatches: number;
    matchesOk: number;
    matchesNoWhitelist: number;
    matchesSammelkunde: number;
    matchesNotFound: number;
    matchesDeleted: number;
    totalBetragNetto: number;
    // ---- Analyse (nachgelagert) ----
    matchesBeraterBlock: number;
    totalDatevRechnung: number; // Summe aller Blockkopf-Summen
    totalNichtWeiterberechenbar: number;
    totalUebersprungenBerechenbar: number;
    bloeckeMitAbweichung: number;
    // ---- Vollständigkeitskontrolle ----
    /** Anzahl Blöcke mit mindestens einer nicht lesbaren Postenzeile */
    bloeckeMitUngeparstenZeilen: number;
    /** Anzahl nicht lesbarer Postenzeilen über die gesamte Rechnung */
    totalUngeparsteZeilen: number;
    /** Nettobetrag laut Rechnungsfuß; null wenn nicht lesbar */
    nettoSummeLautPdf: number | null;
    /**
     * Differenz totalDatevRechnung - nettoSummeLautPdf; null wenn innerhalb
     * Toleranz oder wenn der Nettobetrag nicht gelesen werden konnte.
     */
    gesamtAbweichung: number | null;
  };
  /** Artikel-Rangliste, absteigend nach Nettosumme */
  artikelRanking: ArtikelRankingEntry[];
  /** Kunden-Rangliste, absteigend nach DATEV-Kopfsumme */
  kundenRanking: KundenRankingEntry[];
}

/**
 * Steuerung der Ausführung.
 */
export interface ExecuteOptions {
  /**
   * Führt die Weiterberechnung auch dann aus, wenn die Vollständigkeitsprüfung
   * Befunde gemeldet hat. Nur für den bewussten Ausnahmefall gedacht; ohne diese
   * Angabe bricht die Ausführung ab, ohne etwas zu schreiben.
   */
  trotzBefundenAusfuehren?: boolean;
}

/**
 * Ergebnis der Ausführung
 */
export interface ExecutionResult {
  success: boolean;
  createdInvoices: Array<{ customerId: string; customerCompany: string; invoiceId: string; year: number; month: number }>;
  appendedInvoices: Array<{ customerId: string; customerCompany: string; invoiceId: string; year: number; month: number; positionsAdded: number }>;
  /**
   * Positionen, die nicht eingefügt wurden, weil sie auf der Zielrechnung
   * bereits vorhanden waren. Tritt bei einem Wiederholungslauf derselben
   * DATEV-Rechnung auf und ist dort der Normalfall, kein Fehler.
   */
  skippedPositions: Array<{ customerId: string; customerCompany: string; invoiceId: string; beschreibung: string }>;
  errors: Array<{ customerId: string; customerCompany: string; error: string }>;
}

// ============================================
// DATEV-KOSTEN (Grundlage der Unit Economics)
// ============================================

/**
 * Artikelnummern des Abrechnungsgrundpreises (LuG und LODAS). Ihre Menge ist
 * die Basis, auf der DATEV den Abrechnungsbonus berechnet, und damit auch der
 * Verteilschlüssel für den Bonus auf die Mandanten.
 */
export const ABRECHNUNGSGRUNDPREIS_ARTIKEL: string[] = ['42181', '20537'];

/**
 * Erkennt die Pauschalzeile "Abrechnungsbonus Lohn" im beraterbezogenen Block.
 * Die Zeile hat keine Artikelnummer.
 */
export const ABRECHNUNGSBONUS_PATTERN = /abrechnungsbonus/i;

/**
 * Einordnung eines Blocks der DATEV-Rechnung:
 * - mandant: Kosten eines aktiven Kunden
 * - allgemein: beraterbezogene Leistungen inklusive eigenem Bestand (Mandant 10000)
 * - nicht_zuordenbar: Mandantenblock ohne aktiven Kunden
 */
export type DatevKostenKategorie = 'mandant' | 'allgemein' | 'nicht_zuordenbar';

/**
 * Art der Erfassung: zusammen mit der Weiterberechnung oder ausschliesslich als
 * Kostenerfassung (z.B. für bereits weiterberechnete Vormonate).
 */
export type DatevKostenModus = 'weiterberechnung' | 'nur_kosten';

/** Ein Block der DATEV-Rechnung, so wie er gespeichert wird */
export interface DatevKostenblock {
  datevMandantennummer: string;
  datevName: string;
  kategorie: DatevKostenKategorie;
  customerId: string | null;
  customerCompany: string | null;
  /** Kopfsumme laut DATEV */
  kopfsumme: number;
  /** Kopfsumme ohne Abrechnungsbonus */
  kostenOhneBonus: number;
  /** Anteil am Abrechnungsbonus (negativ) */
  bonusAnteil: number;
  /** Menge der Abrechnungsgrundpreise in diesem Block */
  grundpreisMenge: number;
  positionen: ParsedInvoicePosition[];
}

/** Vollständig berechnete Kostenerfassung einer DATEV-Rechnung */
export interface DatevKostenErfassung {
  beraternummer: string;
  rechnungsnummer: string;
  rechnungsdatum: string | null;
  year: number;
  month: number;
  nettoSumme: number;
  abrechnungsbonus: number;
  grundpreisMenge: number;
  bloecke: DatevKostenblock[];
}

/** Hinweis auf eine bereits gespeicherte DATEV-Rechnung */
export interface DatevKostenBereitsErfasst {
  erfasstAm: string; // ISO-Zeitstempel
  modus: DatevKostenModus;
}
